/**
 * Intro Slice
 *
 * Introduction section with 2x2 stats grid on left, copy + button on right.
 */

import React from "react";
import { graphql } from "gatsby";
import { motion } from "framer-motion";
import { PrismicRichText } from "@prismicio/react";
import Button from "../../components/Button";
import BackgroundStaticVideo from "../../components/BackgroundStaticVideo";
import * as styles from "./Intro.module.scss";

export const Intro = ({ slice }) => {
  const {
    copy,
    button_text,
    button_link,
    button_style,
    color_scheme,
    show_background_static_video,
  } = slice?.primary || {};
  const items = slice?.items || [];

  return (
    <section
      className={styles.intro}
      data-color-scheme={color_scheme || "white"}
      data-background-static-video={show_background_static_video ? "true" : undefined}
      id="intro"
    >
      {show_background_static_video && <BackgroundStaticVideo />}
      <div className={`page-width padding-section-vertical ${styles.container}`}>
        {/* Stats Grid - Left Side */}
        {items.length > 0 && (
          <motion.div
            className={styles.stats}
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {items.map((item, index) => (
              <motion.div
                key={index}
                className={styles.stat}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.1,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              >
                {item.stat_metric && (
                  <span className={styles.statMetric}>{item.stat_metric}</span>
                )}
                {item.stat_unit && (
                  <span className={styles.statUnit}>{item.stat_unit}</span>
                )}
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Content - Right Side */}
        <motion.div
          className={styles.content}
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {copy?.richText && (
            <PrismicRichText
              field={copy.richText}
              components={{
                heading2: ({ children }) => (
                  <h2 className={`h3 ${styles.heading}`}>{children}</h2>
                ),
                paragraph: ({ children }) => (
                  <p className={styles.copy}>{children}</p>
                ),
              }}
            />
          )}
          {button_text && button_link?.url && (
            <div className={styles.buttonWrapper}>
              <Button
                href={button_link.url}
                target={button_link.target}
                variant={button_style || "solid"}
                className={styles.button}
              >
                {button_text}
              </Button>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export const query = graphql`
  fragment HomepageDataBodyIntro on PrismicHomepageDataBodyIntro {
    primary {
      copy {
        richText
        text
      }
      button_text
      button_link {
        url
        target
        link_type
      }
      button_style
      color_scheme
      show_background_static_video
    }
    items {
      stat_metric
      stat_unit
    }
  }
`;
